import React from "react"

const MedicalDecor = () => {
  return (
    <div className="relative h-full w-full overflow-hidden" aria-hidden="true">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-indigo-50" />
      <div className="absolute inset-0">
        <svg className="h-full w-full text-gray-200" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <defs>
            <pattern id="dots2" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="currentColor" />
            </pattern>
            <radialGradient id="halo" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#93c5fd" stopOpacity="0.15"/>
              <stop offset="70%" stopColor="#93c5fd" stopOpacity="0.05"/>
              <stop offset="100%" stopColor="#93c5fd" stopOpacity="0"/>
            </radialGradient>
          </defs>
          <rect width="100%" height="100%" fill="url(#dots2)" />
        </svg>
      </div>

      {/* Circular cluster of medical icons */}
      <div className="absolute inset-0">
        <style>{`
          @keyframes floatY { 0%{ transform: translateY(0) } 50%{ transform: translateY(-8px) } 100%{ transform: translateY(0) } }
          @keyframes sway { 0%{ transform: rotate(0deg) } 50%{ transform: rotate(2deg) } 100%{ transform: rotate(0deg) } }
          @keyframes slow-rotate { 0%{ transform: rotate(0deg) } 100%{ transform: rotate(360deg) } }
          @media (min-width: 768px){ :root{ --ring-size: 260px } }
          @media (max-width: 767px){ :root{ --ring-size: 200px } }
        `}</style>
        {/* Circular container */}
        <div className="absolute left-1/2 top-20 -translate-x-1/2 md:top-24" style={{width: 'var(--ring-size)', height: 'var(--ring-size)'}}>
          <div className="relative h-full w-full" style={{animation:"slow-rotate 55s linear infinite"}}>
            {/* decorative rings */}
            <svg className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" width="100%" height="100%" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="46" fill="url(#halo)"/>
              <circle cx="50" cy="50" r="42" fill="none" stroke="#c7d2fe" strokeOpacity="0.5" strokeDasharray="4 6" />
              <circle cx="50" cy="50" r="28" fill="none" stroke="#e5e7eb" strokeOpacity="0.8" />
            </svg>

            {/* Positions around a circle using rotate/translate trick */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
              {/* Stethoscope at 0deg */}
              <div className="absolute" style={{transform:"rotate(0deg) translate(44%) rotate(0deg)"}}>
                <svg className="h-9 w-9 text-gray-500" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{animation:'floatY 6s ease-in-out infinite'}}>
                  <path d="M18 10v14c0 8 6 12 12 12s12-4 12-12V10"/>
                  <path d="M30 36v10a10 10 0 0020 0v-6"/>
                  <circle cx="52" cy="40" r="4" fill="currentColor" stroke="none"/>
                </svg>
              </div>

              {/* Pill at 60deg */}
              <div className="absolute" style={{transform:"rotate(60deg) translate(44%) rotate(-60deg)"}}>
                <svg className="h-8 w-8 text-gray-400" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{animation:'sway 8s ease-in-out infinite'}}>
                  <rect x="8" y="28" rx="10" ry="10" width="24" height="20" fill="currentColor" opacity="0.25" stroke="none"/>
                  <rect x="32" y="16" rx="10" ry="10" width="24" height="20" />
                  <path d="M20 38l24-12" />
                </svg>
              </div>

              {/* Syringe at 120deg */}
              <div className="absolute" style={{transform:"rotate(120deg) translate(44%) rotate(-120deg)"}}>
                <svg className="h-8 w-8 text-gray-500" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'floatY 8s ease-in-out -1s infinite'}}>
                  <path d="M42 10l12 12"/>
                  <path d="M14 38l20-20 12 12-20 20H14v-12z"/>
                  <path d="M22 30l12 12"/>
                </svg>
              </div>

              {/* Heartbeat at 180deg */}
              <div className="absolute" style={{transform:"rotate(180deg) translate(44%) rotate(-180deg)"}}>
                <svg className="h-8 w-8 text-gray-400" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" style={{animation:'floatY 6.5s ease-in-out -0.5s infinite'}}>
                  <path d="M6 30h10l5-8 7 14 5-10h20"/>
                </svg>
              </div>

              {/* Microscope at 240deg */}
              <div className="absolute" style={{transform:"rotate(240deg) translate(44%) rotate(-240deg)"}}>
                <svg className="h-9 w-9 text-gray-500" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'sway 7.5s ease-in-out -1s infinite'}}>
                  <path d="M24 18l12 12"/>
                  <rect x="18" y="14" width="10" height="6" rx="2"/>
                  <path d="M28 30l6 6M14 52h36M18 46h24a8 8 0 018 8"/>
                </svg>
              </div>

              {/* Bandage at 300deg */}
              <div className="absolute" style={{transform:"rotate(300deg) translate(44%) rotate(-300deg)"}}>
                <svg className="h-8 w-8 text-gray-400" viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'floatY 9s ease-in-out -2s infinite'}}>
                  <rect x="12" y="28" width="40" height="12" rx="6"/>
                  <circle cx="24" cy="34" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="30" cy="34" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="36" cy="34" r="1.5" fill="currentColor" stroke="none"/>
                  <circle cx="42" cy="34" r="1.5" fill="currentColor" stroke="none"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subtle caption removed for clean look */}
    </div>
  )
}

export default MedicalDecor


